<template>
  <div
    ref="tablist"
    class="tabs flex"
    :class="{ 'tw:rounded-full tw:bg-denim-100 tw:p-1': type === 'toggles' }"
    data-testid="tabs"
    role="tablist"
    @focusout="onFocusout"
    @keydown="onKeydown"
  >
    <slot />
  </div>
</template>

<script lang="ts">
export default {
  name: "BaseTabs",
};
</script>

<script setup lang="ts">
import {
  nextTick,
  onBeforeUnmount,
  provide,
  type Ref,
  useTemplateRef,
} from "vue";

import { type Tab } from "shared/components/base/BaseTabs/BaseTab.vue";
import { useTabsKeyboard } from "shared/components/base/BaseTabs/useTabsKeyboard";
import { TabsSymbol } from "shared/components/base/symbols";
import type { Nullable } from "shared/types";

export interface Props {
  duration?: number | string;
  type?: "default" | "toggles";
}

export interface TabsProvide {
  currentTab: Ref<Tab["name"]>;
  focusedName: Readonly<Ref<Nullable<Tab["name"]>>>;
  register: (tab: Tab) => void;
  selectTab: (name: Tab["name"]) => void;
  unregister: (tab: Tab) => void;
}

const props = withDefaults(defineProps<Props>(), {
  duration: 250,
  type: "default",
});

const currentTab = defineModel<Tab["name"]>({
  required: true,
});

let animateTimer: ReturnType<typeof setTimeout>;
const tabsList: Tab[] = [];

const tablist = useTemplateRef<HTMLDivElement>("tablist");

const animate = (oldName: string | number, newName: string | number) => {
  const oldTab = oldName ? tabsList.find((tab) => tab.name === oldName) : null;

  const newTab = newName ? tabsList.find((tab) => tab.name === newName) : null;

  if (oldTab?.indicator.value && newTab?.indicator.value) {
    const oldEl = oldTab.indicator.value;
    const newEl = newTab.indicator.value;

    clearTimeout(animateTimer);

    oldEl.style.transition = "none";
    oldEl.style.transform = "none";
    newEl.style.transition = "none";
    newEl.style.transform = "none";

    const oldPos = oldEl.getBoundingClientRect();
    const newPos = newEl.getBoundingClientRect();

    newEl.style.transform = `
      translate3d(${oldPos.left - newPos.left}px,0,0)
    `;

    newEl.style.width = `${oldPos.width}px`;

    nextTick(() => {
      animateTimer = setTimeout(() => {
        newEl.style.transition = `all ${props.duration}ms cubic-bezier(.3, 0, .2, 1)`;
        newEl.style.transform = "none";
        newEl.style.removeProperty("width");
      }, 150);
    });
  }
};

const selectTab: TabsProvide["selectTab"] = (name) => {
  animate(currentTab.value, name);
  currentTab.value = name;
};

const register: TabsProvide["register"] = (tab) => {
  tabsList.push(tab);
};

const unregister: TabsProvide["unregister"] = (tab) => {
  tabsList.splice(tabsList.indexOf(tab), 1);
};

const { focusedName, onFocusout, onKeydown } = useTabsKeyboard({
  currentTab,
  tablist,
  tabsList,
});

onBeforeUnmount(() => {
  clearTimeout(animateTimer);
});

provide(TabsSymbol, {
  currentTab,
  focusedName,
  selectTab,
  register,
  unregister,
});
</script>
